import React, { useState, useEffect } from 'react'
import { Carousel } from '@ynap/carousel'
import ProductSummaryWithAddtoBag from '@ynap/product-summary/src/ProductSummaryWithAddToBag/ProductSummaryWithAddToBag'
import BEMHelper from '@ynap/bem-helper'
import { useProductListing, useWindowSize } from '../../hooks'
import { getItems, trackAddToBag, trackRefresh } from './analytics'

const breakpoints = {
    mobile: 2.5,
    tablet: 4.5,
    desktop: 2,
    'desktop-large': 3,
}

const bem = new BEMHelper('FinishingTouches')

const FinishingTouches = (props) => {
    try {
        const {
            messages,
            addToBagByProductColour,
            addToBagState,
            tracking,
            cartReady,
            globalConfig,
            locale,
            config,
            excludeIds,
            toggleproductOverlay,
            imagesphereAspectRatio,
        } = props
        const { title, refresh } = messages.finishingTouches
        const { loading: addToCartLoading, error: addToCartError } = addToBagState
        const { disabledCountries, productView } = config
        const { category, categoryIds, maxPrices, maxPageNumber, ...rest } = productView

        if (disabledCountries.includes(locale.country)) {
            return null
        }

        const recentPage = typeof window !== 'undefined' ? parseInt(window.localStorage.getItem('finishingTouchesPage') ?? '0') : 0
        const [lastAddedIndex, setLastAddedIndex] = useState(-1)
        const [pageNumber, setPageNumber] = useState(recentPage < maxPageNumber ? recentPage + 1 : 1)
        const [index, setIndex] = useState(0)
        const [noOfProducts, setNoOfProducts] = useState(0)

        useEffect(() => {
            if (typeof window !== 'undefined') {
                window.localStorage.setItem('finishingTouchesPage', pageNumber.toString())
            }
        }, [pageNumber])

        const options = {
            addCategoryId: categoryIds[locale.country] ?? categoryIds.default,
            pageNumber: pageNumber,
            maxPrice: maxPrices[locale.currency] ?? maxPrices.default,
            ...rest,
        }

        const {
            products,
            loading: productsLoading,
            error: productsError,
        } = useProductListing({
            path: category,
            options,
            locale,
            messages,
            globalConfig,
            imagesphereAspectRatio,
        })
        const breakpoint = useWindowSize()

        useEffect(() => {
            if (!productsLoading && !productsError) {
                tracking?.trackEvent({
                    event: 'upsell list impression',
                    effect: 'show upsell products',
                    category: {
                        primaryCategory: 'ecommerce',
                        subCategory1: 'finishing touches',
                        subCategory2: 'carousel - finishing touches',
                    },
                    listing: {
                        item: getItems({ products }),
                    },
                })
            }
        }, [productsLoading])

        useEffect(() => {
            setNoOfProducts(products.length)
        }, [products])

        useEffect(() => {
            if (!addToCartLoading && !addToCartError && noOfProducts !== 0 && lastAddedIndex !== -1) {
                const newNoProducts = noOfProducts - 1
                const requiresIndexShift = index > 0 && index > newNoProducts - Math.floor(breakpoints[breakpoint]) // or would show whitespace
                
                setNoOfProducts(newNoProducts)
                if (requiresIndexShift) {
                    setIndex(index - 1)
                }
            }
        }, [addToCartLoading, addToCartError])

        return (
            <div className={bem(null, { hideProducts: productsError })}>
                <h3 className={bem('title')}>{title()}</h3>
                <Carousel
                    slidesToShow={breakpoints[breakpoint]}
                    pageComponent={null}
                    classNames={{ container: bem('carousel'), slideClass: bem('slide') }}
                    defaultIndex={index}
                    onPrevClick={() => setIndex(index - 1)}
                    onNextClick={() => setIndex(index + 1)}
                >
                    {products
                        .filter((product) => !excludeIds.includes(product?.skuPartNumber))
                        .map((product, index) => {
                            return (
                                <ProductSummaryWithAddtoBag
                                    className={bem('carousel-item')}
                                    key={product.skuPartNumber ?? index}
                                    skuPartNumber={product.skuPartNumber}
                                    product={{ ...product, isBuyable: !addToCartLoading }}
                                    messages={messages}
                                    addToCart={(...args) => {
                                        setLastAddedIndex(index)
                                        return trackAddToBag({ args, addToBagByProductColour, tracking, product, index })
                                    }}
                                    addToCartLoading={index === lastAddedIndex && addToCartLoading}
                                    addToCartError={addToCartError}
                                    openProductDetail={() => toggleproductOverlay(product.skuPartNumber)}
                                    loading={productsLoading || !cartReady}
                                />
                            )
                        })}
                </Carousel>
                <button
                    className={bem('refresh', { disabled: productsLoading || !cartReady })}
                    onClick={() => {
                        setPageNumber(pageNumber < maxPageNumber ? pageNumber + 1 : 1)
                        trackRefresh(tracking)
                        setIndex(0)
                    }}
                >
                    {refresh()}
                </button>
            </div>
        )
    } catch (error) {
        return null
    }
}

export default FinishingTouches
